import React from 'react';
import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faAngleDown } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import { LOADING_STYLES } from '@components/main-components/skeleton/Skeleton';
import { ChartCardAction } from './Action';
import { useChartCardContext } from './Context';

export function ChartCardActions({
  staticActions,
  hoverActions,
}: {
  /**
   * Actions that are always visible
   */
  staticActions: ReactNode;
  /**
   * Actions that are only showing on hover on tablet and bigger
   * These actions will always be visible on mobile
   */
  hoverActions?: ReactNode;
}) {
  const { isLoadingSummary, minimal } = useChartCardContext();

  return (
    <Container loading={isLoadingSummary}>
      <HoverActions>{hoverActions}</HoverActions>
      <StaticActions loading={isLoadingSummary}>
        {staticActions}
        <div className={clsx(minimal ? 'md:hidden' : 'md:block', 'hidden')}>
          <Arrow>
            <ChartCardAction icon={<FontAwesomeIcon icon={faAngleDown} />} actionSize="L" />
          </Arrow>
        </div>
      </StaticActions>
    </Container>
  );
}

export const HoverActions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 12,
  opacity: 1,

  '@tablet': {
    gap: 8,
    opacity: 0,
  },
});

const StaticActions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 12,

  '@tablet': {
    gap: 8,
  },

  variants: {
    loading: {
      true: {
        '> *': {
          ...LOADING_STYLES,
          borderRadius: '$extraLarge',
          aspectRatio: '1 / 1',
          height: 'fit-content',
        },
      },
    },
  },
});

const Container = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  paddingRight: 0,
  marginLeft: 'auto',

  '@tablet': {
    gap: 8,
    paddingRight: 16,
    marginLeft: 32,
  },

  '&:hover': {
    [`> ${HoverActions}`]: {
      opacity: 1,
    },
  },

  variants: {
    loading: {
      true: {
        pointerEvents: 'none',
        svg: {
          opacity: 0,
        },
      },
    },
  },
});

export const Arrow = styled('div', {
  transition: 'transform 0.5s ease',
});
